/**
 * Seção da ficha do foco: o histórico de uso e cobertura do solo (MapBiomas,
 * 1985–2025) no círculo de 375 m em volta do foco e os imóveis do CAR que
 * contêm o ponto. Pedida só quando a ficha abre (com economia de dados, só no
 * botão).
 */
import { Info } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatInt } from '../lib/format';
import { formatDecimal } from '../fires/format';
import { BASE_TERRENO, buscarTerreno, dentroDaArea, type ComposicaoAno, type ImovelCar, type Terreno } from './api';
import { useMapInstance } from '../map/mapContext';
import { aproximarDoImovel } from './aproximar';
import { evitarTambemOImovel } from '../fires/areaDoFoco';
import { CONSULTA_CELULAR } from '../map/popupArea';
import { corDoImovel, extensaoDoImovel, mostrarTerreno, realcarImovel } from './destaque';
import { MAPBIOMAS_URL } from './legenda';
import { PainelCar } from './PainelCar';
import {
  classesDestaque,
  corDaFatia,
  fatiasDoAno,
  fracNativa,
  nomeDaFatia,
  resumoDoAno,
  statusCar,
  tipoCar,
} from './serie';

const noCelular = () => typeof window !== 'undefined' && !!window.matchMedia?.(CONSULTA_CELULAR).matches;

type Estado = { tipo: 'parado' } | { tipo: 'carregando' } | { tipo: 'erro' } | { tipo: 'pronto'; dado: Terreno };

function useTerreno(lat: number, lon: number, uf: string | null | undefined, ligado: boolean, tentativa: number): Estado {
  const [estado, setEstado] = useState<Estado>({ tipo: 'parado' });
  useEffect(() => {
    if (!ligado) return setEstado({ tipo: 'parado' });
    let vivo = true;
    setEstado({ tipo: 'carregando' });
    buscarTerreno(lat, lon, uf)
      .then((dado) => vivo && setEstado({ tipo: 'pronto', dado }))
      .catch(() => vivo && setEstado({ tipo: 'erro' }));
    return () => {
      vivo = false;
    };
  }, [lat, lon, uf, ligado, tentativa]);
  return estado;
}

function Titulo(props: { children: string; sub?: string }) {
  return (
    <div className="mb-1.5">
      <div className="text-xs font-medium text-ink-200">{props.children}</div>
      {props.sub && <div className="text-[11px] text-ink-400">{props.sub}</div>}
    </div>
  );
}

function pct(frac: number): string {
  return `${Math.round(frac * 100)}%`;
}

export function SerieUsoSolo(props: { anos: number[]; comp: ComposicaoAno[] }) {
  const { anos, comp } = props;
  const destaque = useMemo(() => classesDestaque(comp), [comp]);
  const barras = useMemo(() => comp.map((c) => fatiasDoAno(c, destaque)), [comp, destaque]);
  const ultimo = anos.length - 1;
  const [sel, setSel] = useState(ultimo);
  const i = Math.min(sel, ultimo);
  const nativaIni = fracNativa(comp[0]);
  const nativaFim = fracNativa(comp[ultimo]);
  const temOutras = barras.some((b) => b.some((f) => f.codigo === -1));

  const prender = (k: number) => setSel(Math.max(0, Math.min(ultimo, k)));
  const escolher = (e: React.PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    prender(Math.floor(((e.clientX - r.left) / r.width) * anos.length));
  };
  const teclar = (e: React.KeyboardEvent<SVGSVGElement>) => {
    const passo = { ArrowLeft: -1, ArrowRight: 1, Home: -anos.length, End: anos.length }[e.key];
    if (passo === undefined) return;
    e.preventDefault();
    prender(i + passo);
  };

  return (
    <>
      <svg
        viewBox={`0 0 ${anos.length} 100`}
        preserveAspectRatio="none"
        // O dedo que arrasta na vertical rola a ficha; o toque e o arraste lateral escolhem o ano.
        className="block h-12 w-full cursor-crosshair touch-pan-y rounded-sm focus-visible:outline-2 focus-visible:outline-mata-400"
        role="img"
        tabIndex={0}
        aria-label={`Uso do solo de ${anos[0]} a ${anos[ultimo]}; setas escolhem o ano. ${anos[0]}: ${resumoDoAno(comp[0])}. ${anos[ultimo]}: ${resumoDoAno(comp[ultimo])}.`}
        onKeyDown={teclar}
        onPointerMove={escolher}
        onPointerDown={escolher}
        onPointerLeave={(e) => e.pointerType === 'mouse' && setSel(ultimo)}
        data-testid="terreno-serie"
      >
        {barras.map((fatias, k) => {
          let y = 0;
          return (
            <g key={anos[k]} opacity={k === i ? 1 : 0.7}>
              {fatias.map((f) => {
                const h = f.frac * 100;
                const r = <rect key={f.codigo} x={k + 0.06} y={y} width={0.88} height={h} fill={corDaFatia(f.codigo)} />;
                y += h;
                return r;
              })}
            </g>
          );
        })}
        <rect x={i} y={0} width={1} height={100} fill="none" stroke="#fff" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="mt-0.5 flex justify-between text-[10px] text-ink-400" aria-hidden>
        <span>{anos[0]}</span>
        <span>{anos[Math.floor(ultimo / 2)]}</span>
        <span>{anos[ultimo]}</span>
      </div>
      <p className="mt-1 text-ink-100">
        <span className="font-semibold text-white">{anos[i]}</span> · {resumoDoAno(comp[i])}
      </p>
      {nativaIni != null && nativaFim != null && (nativaIni > 0 || nativaFim > 0) && (
        <p className="mt-0.5 text-ink-300">
          Vegetação nativa: {pct(nativaIni)} em {anos[0]} → {pct(nativaFim)} em {anos[ultimo]}
        </p>
      )}
      <ul className="mt-1.5 flex flex-wrap gap-x-2.5 gap-y-0.5 text-[10px] text-ink-300">
        {[...destaque, ...(temOutras ? [-1] : [])].map((c) => (
          <li key={c} className="inline-flex items-center gap-1">
            <span className="inline-block size-2 shrink-0 rounded-[2px]" style={{ background: corDaFatia(c) }} aria-hidden />
            {nomeDaFatia(c)}
          </li>
        ))}
      </ul>
    </>
  );
}

const TIPO_CURTO: Record<string, string> = { IRU: 'Imóvel rural', AST: 'Assentamento', PCT: 'Povos tradicionais' };

function hectares(ha: number): string {
  return `${ha < 100 ? formatDecimal(ha, 1) : formatInt(Math.round(ha))} ha`;
}

function Imovel(props: {
  imovel: ImovelCar;
  cor: string;
  selecionado: boolean;
  infoAberta: boolean;
  onSelecionar: () => void;
  onInfo: () => void;
  onRealce: (cod: string | null) => void;
}) {
  const { imovel: i, cor, selecionado } = props;
  // O clique na linha aproxima o mapa do imóvel; o "i" dela abre o painel ao lado com o
  // código, a situação explicada e o link para o SICAR.
  const partes = [(i.tipo && TIPO_CURTO[i.tipo]) || tipoCar(i), i.areaHa != null ? hectares(i.areaHa) : null, statusCar(i)];
  const nome = partes.filter(Boolean).join(' · ');
  return (
    <li className="flex items-center gap-0.5">
      <button
        type="button"
        className={`flex min-w-0 flex-1 items-center gap-1.5 rounded-sm py-0.5 text-left hover:bg-white/5 ${selecionado ? 'bg-white/10' : ''} ${i.status === 'CA' ? 'text-ink-400' : 'text-ink-100'}`}
        aria-pressed={selecionado}
        title={`${i.cod} · aproximar o mapa`}
        onClick={props.onSelecionar}
        onPointerEnter={() => props.onRealce(i.cod)}
        onPointerLeave={() => props.onRealce(null)}
        onFocus={() => props.onRealce(i.cod)}
        onBlur={() => props.onRealce(null)}
      >
        <span
          className="inline-block h-2.5 w-3 shrink-0 rounded-[2px] border-2"
          style={{ borderColor: cor, background: `${cor}33`, borderStyle: i.status === 'CA' ? 'dashed' : 'solid' }}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate">{nome}</span>
        {i.contem && (
          <span className="shrink-0 text-[10px] text-amber-300" title="O ponto do foco cai dentro deste imóvel">
            no ponto
          </span>
        )}
      </button>
      <button
        type="button"
        aria-label={`Detalhes do imóvel: ${nome}`}
        title="Detalhes e link para o SICAR"
        aria-pressed={props.infoAberta}
        className={`inline-flex size-6 shrink-0 items-center justify-center rounded-md hover:bg-ink-700 hover:text-white ${props.infoAberta ? 'bg-ink-700 text-white' : 'text-ink-400'}`}
        onClick={props.onInfo}
        onPointerEnter={() => props.onRealce(i.cod)}
        onPointerLeave={() => props.onRealce(null)}
      >
        <Info className="size-3.5" aria-hidden />
      </button>
    </li>
  );
}

const MAX_LINHAS = 4;


function SecaoCar(props: { car: Terreno['car']; lat: number; lon: number; semTitulo?: boolean }) {
  const { car } = props;
  const map = useMapInstance((s) => s.map);
  const [todos, setTodos] = useState(false);
  // Painel ao lado: null fechado; cod null = só a explicação geral.
  const [painel, setPainel] = useState<{ cod: string | null } | null>(null);
  const [ancora, setAncora] = useState<HTMLElement | null>(null);
  const fecharPainel = useCallback(() => setPainel(null), []);
  // O imóvel de que o mapa se aproximou (clique na linha).
  const [selecionado, setSelecionado] = useState<string | null>(null);
  // Realçado no mapa: o do painel aberto, ou o selecionado.
  const escolhido = painel?.cod ?? selecionado;
  // O imóvel do painel fica realçado no mapa; o mouse em outra linha realça aquela enquanto estiver nela.
  useEffect(() => {
    if (!map) return;
    realcarImovel(map, escolhido);
    // Saindo da aba (celular) ou fechando a ficha, o realce não fica preso no mapa.
    return () => realcarImovel(map, null);
  }, [map, escolhido]);
  // A ficha do foco (desktop) também sai de cima do imóvel selecionado, de que o mapa se aproximou.
  useEffect(() => {
    const extensao = map && selecionado ? extensaoDoImovel(map, selecionado) : null;
    if (!extensao) return;
    evitarTambemOImovel(props.lat, props.lon, extensao);
    return () => evitarTambemOImovel(props.lat, props.lon, null);
  }, [map, selecionado, props.lat, props.lon]);
  if (car.situacao === 'sem_uf')
    return <p className={props.semTitulo ? 'text-ink-400' : 'mt-2.5 text-ink-400'}>CAR não consultado: estado do foco não identificado.</p>;
  const lista = todos ? car.imoveis : car.imoveis.slice(0, MAX_LINHAS);
  const resto = car.imoveis.length - lista.length;
  const realce = (cod: string | null) => map && realcarImovel(map, cod ?? escolhido);
  return (
    <div className={props.semTitulo ? '' : 'mt-2.5'} data-testid="terreno-car" ref={setAncora}>
      <div className="flex items-start justify-between gap-2">
        {props.semTitulo ? (
          car.imoveis.length > 0 && <p className="mb-1 text-[11px] text-ink-400">no raio de 375 m · contornados no mapa</p>
        ) : (
          <Titulo sub={car.imoveis.length ? 'no raio de 375 m · contornados no mapa' : undefined}>Cadastro Ambiental Rural</Titulo>
        )}
        <button
          type="button"
          aria-label="O que é o CAR e de onde vêm os dados"
          title="O que é o CAR e de onde vêm os dados"
          aria-pressed={painel?.cod === null}
          className="-mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-md text-ink-300 hover:bg-ink-700 hover:text-white"
          onClick={() => setPainel((p) => (p && p.cod === null ? null : { cod: null }))}
        >
          <Info className="size-3.5" aria-hidden />
        </button>
      </div>
      {car.situacao === 'indisponivel' && <p className="text-ink-400">O SICAR não respondeu agora.</p>}
      {car.situacao === 'ok' && car.imoveis.length === 0 && <p className="text-ink-300">Nenhum imóvel do CAR no raio.</p>}
      {lista.length > 0 && (
        <ul>
          {lista.map((im, k) => (
            <Imovel
              key={im.cod}
              imovel={im}
              cor={corDoImovel(k)}
              selecionado={selecionado === im.cod}
              infoAberta={painel?.cod === im.cod}
              onInfo={() => setPainel((p) => (p?.cod === im.cod ? null : { cod: im.cod }))}
              onSelecionar={() => {
                setSelecionado(im.cod);
                // O mapa se aproxima do imóvel (de novo, a cada clique: o usuário pode ter se afastado).
                const extensao = map ? extensaoDoImovel(map, im.cod) : null;
                if (!map || !extensao) return;
                // No desktop a ficha sai sozinha de cima do imóvel (ver o efeito acima). No celular
                // ela não desvia: o zoom é em volta do foco, que fica parado, na faixa livre ao lado dela.
                if (!noCelular()) return aproximarDoImovel(map, extensao, props.lat, props.lon);
                const ficha = ancora?.closest<HTMLElement>('.amaview-popup');
                aproximarDoImovel(map, extensao, props.lat, props.lon, ficha ? [ficha] : [], { focoParado: true });
              }}
              onRealce={realce}
            />
          ))}
        </ul>
      )}
      {resto > 0 && (
        <button type="button" className="mt-0.5 text-mata-300 hover:text-mata-400 hover:underline" onClick={() => setTodos(true)}>
          +{formatInt(resto)} {resto === 1 ? 'imóvel' : 'imóveis'}
        </button>
      )}
      {car.imoveis.length > 0 && (
        <p className="mt-1 text-[11px] text-ink-400">Autodeclarado; não prova posse nem autoria do fogo.</p>
      )}
      {painel && (
        <PainelCar
          ancora={ancora}
          imovel={car.imoveis.find((x) => x.cod === painel.cod) ?? null}
          cor={painel.cod ? corDoImovel(car.imoveis.findIndex((x) => x.cod === painel.cod)) : null}
          lat={props.lat}
          lon={props.lon}
          onFechar={fecharPainel}
        />
      )}
    </div>
  );
}

/** O terreno da ficha de um foco: o pedido, o estado e o destaque no mapa (um por ficha). */
export interface TerrenoDoFoco {
  atende: boolean;
  estado: Estado;
  pedido: boolean;
  pedir: () => void;
  tentarDeNovo: () => void;
}

/**
 * `uf` undefined = ainda estimando (foco sem município): espera, para não pedir
 * duas vezes (sem e com o CAR). null = sem UF conhecida (vem só o MapBiomas).
 * O círculo aparece no mapa com a ficha; os contornos dos imóveis, só com
 * `mostrarImoveis` (a aba CAR aberta) e depois que chegam. Some quando a ficha
 * fecha.
 */
export function useTerrenoDoFoco(
  lat: number,
  lon: number,
  uf: string | null | undefined,
  saveData: boolean,
  mostrarImoveis = true,
): TerrenoDoFoco {
  const [pedido, setPedido] = useState(!saveData);
  const [tentativa, setTentativa] = useState(0);
  const atende = !!BASE_TERRENO && dentroDaArea(lat, lon);
  const estado = useTerreno(lat, lon, uf, atende && pedido && uf !== undefined, tentativa);
  const map = useMapInstance((s) => s.map);
  const imoveis = mostrarImoveis && estado.tipo === 'pronto' ? estado.dado.car.imoveis : null;
  useEffect(() => {
    if (!map || !atende) return;
    return mostrarTerreno(map, lat, lon, 375, imoveis ?? []);
  }, [map, atende, lat, lon, imoveis]);
  const pedir = useCallback(() => setPedido(true), []);
  const tentarDeNovo = useCallback(() => setTentativa((n) => n + 1), []);
  return { atende, estado, pedido, pedir, tentarDeNovo };
}

/** Enquanto não há dado: o botão (economia de dados), o carregando ou o erro. Null quando pronto. */
function Espera(props: { t: TerrenoDoFoco; rotulo: string }) {
  const { estado, pedido } = props.t;
  if (estado.tipo === 'parado' && pedido) return <p className="text-ink-400">carregando…</p>;
  if (estado.tipo === 'parado')
    return (
      <button type="button" className="text-mata-300 hover:text-mata-400 hover:underline" onClick={props.t.pedir}>
        {props.rotulo}
      </button>
    );
  if (estado.tipo === 'carregando')
    return (
      <div className="space-y-1.5" aria-busy="true">
        <div className="h-12 animate-pulse rounded-sm bg-ink-700/60" />
        <p className="text-ink-400">carregando…</p>
      </div>
    );
  if (estado.tipo === 'erro')
    return (
      <p className="text-ink-400">
        Não foi possível carregar agora.{' '}
        <button type="button" className="text-mata-300 hover:text-mata-400 hover:underline" onClick={props.t.tentarDeNovo}>
          Tentar de novo
        </button>
      </p>
    );
  return null;
}

function FonteMapBiomas(props: { colecao: string }) {
  return (
    <a href={MAPBIOMAS_URL} target="_blank" rel="noopener noreferrer" className="hover:text-ink-200 hover:underline">
      MapBiomas Coleção {props.colecao}
    </a>
  );
}

/** O histórico do uso do solo (a aba do celular), com a fonte. */
export function UsoDoSoloDoFoco(props: { t: TerrenoDoFoco }) {
  const { estado } = props.t;
  if (estado.tipo !== 'pronto') return <Espera t={props.t} rotulo="Carregar o histórico do uso do solo" />;
  const mb = estado.dado.mapbiomas;
  if (!mb || !mb.anos.length) return <p className="text-ink-400">Ponto fora da área mapeada.</p>;
  return (
    <div data-testid="terreno">
      <p className="mb-1.5 text-[11px] text-ink-400">raio de 375 m em volta do foco</p>
      <SerieUsoSolo anos={mb.anos} comp={mb.comp} />
      <p className="mt-2 text-[11px] text-ink-400">
        Fonte: <FonteMapBiomas colecao={mb.colecao} />
      </p>
    </div>
  );
}

/** Os imóveis do CAR (a aba do celular), com a fonte. */
export function CarDoFoco(props: { t: TerrenoDoFoco; lat: number; lon: number }) {
  const { estado } = props.t;
  if (estado.tipo !== 'pronto') return <Espera t={props.t} rotulo="Carregar os imóveis do CAR" />;
  return (
    <>
      <SecaoCar car={estado.dado.car} lat={props.lat} lon={props.lon} semTitulo />
      {estado.dado.car.situacao !== 'sem_uf' && <p className="mt-2 text-[11px] text-ink-400">Fonte: SICAR/SFB</p>}
    </>
  );
}
